// Full-screen "Onneksi olkoon" success page shown after the customer accepts an
// offer (request.status === "accepted"). Mirrors the autojerry success layout
// minus "Kutsu kavereita" / "Hae huoltorahoitusta". Contact + opening hours come
// from the accepted offer's revealed fields (GET /:id/offers).
//
// Spec: docs/superpowers/specs/2026-05-25-betonijerry-accept-success-and-provider-jerry-settings-design.md

import { useMemo } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router";
import FrostedCard from "src/components/quote/FrostedCard";
import StepHeader from "src/components/quote/StepHeader";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import { gate2Fallback } from "src/utils/legal/acceptanceCopy";
import { useT, useTRich } from "src/i18n";

export default function ValuAcceptedScreen({ acceptedOffer, copy }) {
    const theme = useTheme();
    const t = useT();
    const tRich = useTRich();
    // Defensive fallback — ValuDetail always passes a real copy (from its own
    // useAcceptanceCopy hook), so this only guards a caller that forgets to.
    const fallbackCopy = useMemo(() => gate2Fallback(t), [t]);
    const effectiveCopy = copy ?? fallbackCopy;
    const easing = theme.custom?.wizard?.motion?.easing ?? "cubic-bezier(0.22, 1, 0.36, 1)";
    const name = acceptedOffer?.jerryContactName;
    const phone = acceptedOffer?.jerryContactPhone;
    const hours = acceptedOffer?.openingHours;

    return (
        <Box sx={{ maxWidth: 720, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 } }}>
            <FrostedCard
                blur={28}
                sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    // Binding-acceptance payoff — the card rises in once, matching
                    // KiitosScreen's terminal-success beat. Restrained: this is a
                    // real-money contract moment, not a confetti screen.
                    animation: `bjAcceptedIn 360ms ${easing}`,
                    "@keyframes bjAcceptedIn": {
                        from: { opacity: 0, transform: "translateY(8px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}
            >
                <Stack spacing={2.5} sx={{ alignItems: "flex-start" }}>
                    <StepHeader>{t("valut.acceptedScreen.heading")}</StepHeader>

                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                        {acceptedOffer?.asiakasNimi
                            ? tRich("valut.acceptedScreen.contactNamed", { name: <strong>{acceptedOffer.asiakasNimi}</strong> })
                            : t("valut.acceptedScreen.contactGeneric")}
                        {" "}{effectiveCopy.ctaCaption}.
                    </Typography>

                    <Box sx={{
                        // The "who will call you" reassurance lands a beat after the
                        // card settles — the structural parallel to KiitosScreen's
                        // delayed check-confirm. Empty (no revealed contact) = no-op.
                        animation: `bjAcceptedDetail 380ms ${easing} 120ms backwards`,
                        "@keyframes bjAcceptedDetail": {
                            from: { opacity: 0, transform: "translateY(5px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                    }}>
                        {name && (
                            <Typography variant="body1">{tRich("valut.acceptedScreen.contactPersonLabel", { name: <strong>{name}</strong> })}</Typography>
                        )}
                        {phone && (
                            <Typography variant="body1">
                                {t("valut.acceptedScreen.phoneLabel")}{" "}
                                <Box component="a" href={`tel:${phone}`} sx={{ color: "inherit", fontWeight: 700 }}>
                                    {phone}
                                </Box>
                            </Typography>
                        )}
                        {hours && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("valut.acceptedScreen.hoursLabel", { hours })}</Typography>
                        )}
                    </Box>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%" }}>
                        <Button component={RouterLink} to="/valut" variant="outlined">
                            {t("valut.acceptedScreen.backToMyValut")}
                        </Button>
                        <Button component={RouterLink} to="/tilaa" variant="contained" sx={amberCtaSx}>
                            {t("valut.shared.newRequestCta")}
                        </Button>
                    </Stack>
                </Stack>
            </FrostedCard>
        </Box>
    );
}
